// src/onboarding/components/OptionButton.js
import React from 'react';
import { useTintableSvg } from '../../utils/Onboarding/tintableSvg';

export default function OptionButton({
  label,
  icon,
  selected = false,
  onClick,
  disabled = false,
  size = 'default',
  gridLayout = false,
  tintable = false,
  iconSizeClass,
  iconStyle,  
  iconContainerClass, // ðŸ‘ˆ fixed container (same for all options on a slide)
  className = '',
}) {
  const sizeClasses = {
    default: 'py-3 px-4 text-sm',
    small: 'py-3 px-4 text-sm',
  };

  const iconBox = gridLayout
    ? (size === 'small' ? 'w-7 h-7' : 'w-10 h-10')
    : (size === 'small' ? 'w-5 h-5' : 'w-4 h-4');

  const iconBoxFinal = iconSizeClass || iconBox;

  // Container gets a visible border
  const iconContainerFinal =
    (iconContainerClass ? iconContainerClass : iconBoxFinal) +
    ' flex items-center justify-center shrink-0 rounded-md overflow-hidden';

  const emojiSize = gridLayout ? 'text-2xl' : 'text-xl';

  const isUrlLike = (s) =>
    typeof s === 'string' &&
    (s.startsWith('/') || s.startsWith('http') || s.startsWith('data:') || s.includes('.'));

  const looksLikeSvgUrl =
    typeof icon === 'string' && icon.trim().toLowerCase().endsWith('.svg');
  const tintableSvgMarkup = useTintableSvg(tintable && looksLikeSvgUrl ? icon : null);

  const renderIcon = () => {
    if (!icon) return null;

    // If caller passed a React element (<svg/> or <img/>), just render it
    if (React.isValidElement(icon)) {
      return React.cloneElement(icon, {
        className: `${iconBoxFinal} object-contain shrink-0 ${icon.props.className || ''}`.trim(),
        style: { ...(icon.props.style || {}), ...(iconStyle || {}) },        'aria-hidden': 'true',
      });
    }

    // Tintable SVG icons: prefer inline markup, fall back to CSS mask while cache warms
    if (tintable && looksLikeSvgUrl) {
      if (tintableSvgMarkup) {
        return (
          <span
            className={`${iconBoxFinal} inline-block shrink-0`}
            style={{ color: 'currentColor', ...(iconStyle || {}) }}
            aria-hidden="true"
            dangerouslySetInnerHTML={{ __html: tintableSvgMarkup }}
          />
        );
      }

      return (
        <span
          className={`${iconBoxFinal} inline-block shrink-0`}
          style={{
            ...(iconStyle || {}),
            WebkitMaskImage: `url(${icon})`,
            WebkitMaskRepeat: 'no-repeat',
            WebkitMaskPosition: 'center',
            WebkitMaskSize: 'contain',
            maskImage: `url(${icon})`,
            maskRepeat: 'no-repeat',
            maskPosition: 'center',
            maskSize: 'contain',
            backgroundColor: 'currentColor',
          }}
          aria-hidden="true"
        />
      );
    }

    // Non-tintable: image URL via <img>
    if (isUrlLike(icon)) {
      return (
        <img
          src={icon}
          alt=""
          className={`${iconBoxFinal} object-contain max-w-full max-h-full shrink-0`}
          style={iconStyle}
          aria-hidden="true"
          decoding="async"
        />
      );
    }

    // Fallback: emoji/text
    return (
      <span className={`${emojiSize} leading-none`} aria-hidden="true">
        {icon}
      </span>
    );
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`
        w-full ${sizeClasses[size]}
        border rounded-lg 
        font-medium transition-all duration-200
        focus:outline-none
        disabled:opacity-50 disabled:cursor-not-allowed
        ${selected
          ? 'bg-mm_black text-white hover:bg-gray-800 border-transparent'
          : 'bg-gray-50 text-mm_black hover:bg-gray-100 hover:border-gray-300 border-gray-200 '
        }
        ${className}
      `}
    >
      <div
        className={
          gridLayout
            ? 'flex flex-col items-center justify-center space-y-2 '
            : 'flex items-center justify-start space-x-4'
        }
      >
        {/* Icon container with a visible border */}
        <div className={iconContainerFinal}>
          {renderIcon()}
        </div>

        <span className={gridLayout ? 'text-center text-sm' : 'text-left'}>
          {label}
        </span>
      </div>
    </button>
  );
}
